import React, { Component } from 'react'
import { connect } from 'react-redux'
import { NavLink, Redirect, Route, Switch, withRouter } from 'react-router-dom'
import InvoiceRoute from './InvoiceRoute'
import InvoiceItemContainer from './InvoiceItemContainer'
import InvoiceValidationForm from './InvoiceValidationForm'
import InvoiceSearch from './InvoiceSearch'
import { InvoiceService } from '../../../services'
import { startFetch, endFetch } from '../../../actions/generalActions'
import { prefix } from '../../../i18n'
import { TO_VALIDATE_COLUMNS, VALIDATED_COLUMNS } from './invoiceColumns'

const tInvoice = prefix('invoice')

class ValidatorContainer extends Component {
  state = {
    toValidateInvoices: [],
    validatedInvoices: [],
    toValidatePage: 1,
    toValidateTotalPages: null,
    validatedPage: 1,
    validatedTotalPages: null,
    filters: {},
  }

  componentDidMount() {
    const { campaign } = this.props
    this.fetchToValidate(campaign, 1)
    this.fetchValidated(campaign, 1)
  }

  // Every navigation refetches the newly active queue, with the filters
  // currently typed in the search bar (it sits above the tabs, so they follow
  // the validator from one queue to the other), so a queue is never shown from
  // a copy fetched before an invoice was validated.
  componentDidUpdate(prevProps) {
    const { campaign, location } = this.props
    if (!location || !prevProps.location) return
    if (location.pathname === prevProps.location.pathname) return
    this.fetchActiveList(campaign, location.pathname, this.state.filters)
  }

  fetchActiveList = (campaign, pathname, filters) => {
    if (/^\/invoice\/validated\/?$/.test(pathname))              this.fetchValidated(campaign, 1, filters)
    else if (/^\/invoice(\/to_validate)?\/?$/.test(pathname))    this.fetchToValidate(campaign, 1, filters)
  }

  fetchToValidate = (campaign, page, filters = this.state.filters) => {
    const { client } = this.props
    this.props.startFetch()
    // Invoices waiting for validator approval → index_waiting_approval endpoint.
    InvoiceService.getWaitingApprovalInvoices({ clientId: client.slug, campaignId: campaign.slug, filters, page })
      .then(response => {
        const { invoices, pagination } = response.data
        this.setState({
          toValidateInvoices: invoices,
          toValidateTotalPages: pagination ? pagination.total_pages : null,
          toValidatePage: page,
        })
        this.props.endFetch()
      })
      .catch(() => this.props.endFetch())
  }

  fetchValidated = (campaign, page, filters = this.state.filters) => {
    const { client } = this.props
    this.props.startFetch()
    // Already validated invoices → index_verified endpoint.
    InvoiceService.getVerifiedInvoices({ clientId: client.slug, campaignId: campaign.slug, filters, page })
      .then(response => {
        const { invoices, pagination } = response.data
        this.setState({
          validatedInvoices: invoices,
          validatedTotalPages: pagination ? pagination.total_pages : null,
          validatedPage: page,
        })
        this.props.endFetch()
      })
      .catch(() => this.props.endFetch())
  }

  handleValidatorPageUpdate = (path) => ({ selected }) => {
    const { campaign } = this.props
    const page = selected + 1
    if (path.includes('to_validate')) this.fetchToValidate(campaign, page)
    if (path.includes('validated'))   this.fetchValidated(campaign, page)
  }

  // DefaultTemplate keeps both queues in the store and moves the invoice
  // between them on validation; here they live in state, so both are refetched.
  handleValidated = () => {
    const { campaign } = this.props
    this.fetchToValidate(campaign, 1)
    this.fetchValidated(campaign, 1)
  }

  // Each queue has its own index_* endpoint, so the search applies to whichever
  // tab is open, as in DefaultTemplate where every tab filters its own list.
  handleSearchRequest = (filters) => {
    const { campaign, location } = this.props
    this.setState({ filters }, () => {
      this.fetchActiveList(campaign, location ? location.pathname : '/invoice/to_validate', filters)
    })
  }

  render() {
    const { campaign } = this.props
    const {
      toValidateInvoices, validatedInvoices,
      toValidatePage, toValidateTotalPages,
      validatedPage, validatedTotalPages,
    } = this.state

    const tabs = [
      { label: tInvoice('to-validate'), path: '/invoice/to_validate' },
      { label: tInvoice('validated'), path: '/invoice/validated' },
    ]

    return (
      <Switch>
        {/* ── Invoice detail pages: no header, tabs or search bar, as on the
            salesperson's /invoice/created/:id ── */}
        <Route path="/invoice/to_validate/:id" render={() => (
          <div className="invoice-layout__detail">
            <InvoiceItemContainer
              editable={false}
              linking="/invoice/to_validate"
              campaign={campaign}
              client={this.props.client}
            />
            <InvoiceValidationForm campaign={campaign} onValidated={this.handleValidated} />
          </div>
        )} />
        <Route path="/invoice/validated/:id" render={() => (
          <InvoiceItemContainer
            editable={false}
            linking="/invoice/validated"
            campaign={campaign}
            client={this.props.client}
          />
        )} />

        {/* ── List views ── */}
        <Route render={() => (
          <div className="invoice-layout">
            <div className="invoice-layout-header">
              <h4 className="invoice-layout-header-title">{tInvoice('title')}</h4>
            </div>

            <nav className="invoice-layout-navigation">
              {tabs.map(tab => (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  activeClassName="invoice-layout-navigation-item-active"
                  className="invoice-layout-navigation-item"
                >
                  {tab.label}
                </NavLink>
              ))}
            </nav>

            <InvoiceSearch
              campaign={campaign}
              validator
              onSearchRequest={this.handleSearchRequest}
            />

            <div className="invoice-layout-content">
              {/* Bare /invoice and any path this view does not own (e.g. the
                  salesperson's /invoice/created) fall back to the queue. */}
              <Switch>
                <Route path="/invoice/to_validate" />
                <Route path="/invoice/validated" />
                <Redirect to="/invoice/to_validate" />
              </Switch>

              <InvoiceRoute
                path="/invoice/to_validate"
                columns={TO_VALIDATE_COLUMNS}
                invoices={toValidateInvoices}
                onPageChange={this.handleValidatorPageUpdate}
                currentPage={toValidatePage - 1}
                totalPages={toValidateTotalPages}
              />
              <InvoiceRoute
                path="/invoice/validated"
                columns={VALIDATED_COLUMNS}
                invoices={validatedInvoices}
                onPageChange={this.handleValidatorPageUpdate}
                currentPage={validatedPage - 1}
                totalPages={validatedTotalPages}
              />
            </div>
          </div>
        )} />
      </Switch>
    )
  }
}

const mapStateToProps = (state) => ({
  client: state.userReducer.client,
})

const mapDispatchToProps = (dispatch) => ({
  startFetch: () => dispatch(startFetch()),
  endFetch: () => dispatch(endFetch()),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ValidatorContainer))
